import React, { useEffect, useState } from "react";
import { usePlayer } from "../api/copenhunt/v1/copenhunt_rbt_react";
import "../styles/UserGamePage.css";
import { GameCondition } from "../api/copenhunt/v1/copenhunt_pb";
import { useGameData } from "../hooks/useGameData";
import { useParams } from "react-router-dom";
import OwnTracksConfig from "../components/OwnTracksConfig";
import UserGameView from "../components/UserGameView";

const UserGamePage: React.FC = () => {
  const { gameId, overviewResponse, teamColors, teamNumbers, objectiveShapes } =
    useGameData();
  const { playerId } = useParams<{ playerId: string }>();
  const {
    useName,
    useTeam,
    useCurrentCoordinates,
    useVisibleInfo,
    mutators,
  } = usePlayer({
    id: playerId ?? "",
  });
  const [currentTime, setCurrentTime] = useState(Date.now() / 1000);

  const { response: visibleInfoResponse } = useVisibleInfo({
    gameId: gameId,
  });
  const { response: nameResponse } = useName();
  const { response: teamResponse } = useTeam();
  const { response: coordinatesResponse } = useCurrentCoordinates();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now() / 1000);
    }, 5 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (
    !overviewResponse ||
    !visibleInfoResponse ||
    !nameResponse ||
    !teamResponse ||
    !coordinatesResponse ||
    !playerId
  ) {
    return <div>Loading...</div>;
  }

  if (overviewResponse.condition === GameCondition.FINISHED) {
    return (
      <div className="notifications">
        <h3>Hello {nameResponse.name}</h3>
        <p>This game is finished.</p>
      </div>
    );
  }

  const config = {
    _type: "configuration",
    auth: true,
    username: playerId,
    password: playerId,
    deviceId: gameId,
    url: `${process.env.REACT_APP_REBOOT_URL}/owntracks`,
    port: 443,
    mode: 3,
    tid: playerId.substring(0, 2),
    monitoring: 2, // = Move.
    locatorInterval: 30, // update coordinates every 30 s
  };
  const stringConfig = JSON.stringify(config);
  const encodedConfig = btoa(stringConfig);
  const urlConfig = `owntracks:///config?inline=${encodedConfig}`;
  const gotCoordinates =
    coordinatesResponse.coordinates.length > 0
      ? coordinatesResponse.coordinates[
          coordinatesResponse.coordinates.length - 1
        ]
      : null;
  const delta = currentTime - (gotCoordinates?.timestamp ?? 0);

  if (nameResponse.name === "" && gotCoordinates?.timestamp === 0) {
    if (gameId) {
      window.location.pathname = `/game/${gameId}/register`;
    }
    return null;
  }

  const showConfig = delta > 300 || gotCoordinates?.timestamp === 0;

  return (
    <>
      {showConfig ? (
        <OwnTracksConfig urlConfig={urlConfig} />
      ) : (
        <UserGameView
          visiblePlayers={visibleInfoResponse.visiblePlayers}
          teamColors={teamColors}
          teamNumbers={teamNumbers}
          objectiveShapes={objectiveShapes}
          visibleLocations={visibleInfoResponse.visibleLocations}
          playerId={playerId}
          objectiveId={visibleInfoResponse.objectiveId}
          teamId={teamResponse.teamId}
          teamInfoById={overviewResponse.teamInfoById}
          locationDelaySeconds={overviewResponse.locationDelaySeconds}
          playerName={nameResponse.name}
          onSendPing={mutators.sendPing}
          playerCoordinates={gotCoordinates}
          overview={{
            condition: overviewResponse.condition,
            secondsToNextSwitch: overviewResponse.secondsToNextSwitch,
            isPaused: overviewResponse.isPaused,
            roundDurationSeconds: overviewResponse.roundDurationSeconds,
            secondsToNextPing: overviewResponse.secondsToNextPing,
            pingIntervalSeconds: overviewResponse.pingIntervalSeconds,
            events: overviewResponse.events,
            latestPingTimestamp: overviewResponse.latestPingTimestamp,
            currentTimestamp: overviewResponse.currentTimestamp,
            mode: overviewResponse.mode,
          }}
        />
      )}
    </>
  );
};

export default UserGamePage;
